import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, BookOpen, Download, CheckCircle, Loader2, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopPage = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [myPurchases, setMyPurchases] = useState([]);

  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get('cancelled')) {
      toast.error('Paiement annulé');
    }
  }, [searchParams]);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/shop/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Fetch user's purchases if authenticated
  useEffect(() => {
    const fetchMyPurchases = async () => {
      if (!isAuthenticated || !token) return;
      
      try {
        const response = await axios.get(`${API}/shop/my-purchases`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyPurchases(response.data);
      } catch (error) {
        console.error('Error fetching purchases:', error);
      }
    };

    fetchMyPurchases();
  }, [isAuthenticated, token]);

  const handlePurchase = async (productId) => {
    setPurchasing(productId);
    
    try {
      const payload = {
        origin_url: window.location.origin
      };
      
      if (!isAuthenticated) {
        // Ask for email if not authenticated
        const email = prompt('Entrez votre adresse email pour recevoir le livre :');
        if (!email) {
          setPurchasing(null);
          return;
        }
        payload.email = email;
      }
      
      const response = await axios.post(
        `${API}/shop/checkout?product_id=${productId}`,
        payload,
        isAuthenticated ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
      setPurchasing(null);
    }
  };

  const isPurchased = (productId) => {
    return myPurchases.some(p => p.product_id === productId);
  };

  const getPurchaseId = (productId) => {
    const purchase = myPurchases.find(p => p.product_id === productId);
    return purchase?.id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-sage-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand-50 to-white">
      <Navbar />
      
      <div className="container-app py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            Librairie numérique
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-4">
            Nos livres sur la psychologie
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Découvrez notre sélection de livres numériques pour approfondir votre compréhension 
            de la psychologie et du bien-être mental.
          </p>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-sand-200">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Aucun livre disponible</h3>
            <p className="text-muted-foreground">
              De nouveaux livres seront bientôt disponibles.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div 
                key={product.id}
                className="bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-soft hover:shadow-md transition-all group"
                data-testid={`product-${product.id}`}
              >
                {/* Cover Image */}
                <div className="aspect-[3/4] relative bg-gradient-to-br from-purple-100 to-pink-100">
                  {product.cover_image_url ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${product.cover_image_url}`}
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="w-16 h-16 text-purple-300" />
                    </div>
                  )}
                  
                  {/* Price badge */}
                  <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                    <span className="font-semibold text-sage-700">{product.price}€</span>
                  </div>
                  
                  {/* Purchased badge */}
                  {isPurchased(product.id) && (
                    <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Acheté
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2 group-hover:text-sage-600 transition-colors">
                    {product.title}
                  </h3>
                  
                  {product.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {product.description}
                    </p>
                  )}
                  
                  <div className="text-xs text-purple-600 font-medium mb-4 capitalize">
                    {product.category}
                  </div>
                  
                  {/* Action Button */}
                  {isPurchased(product.id) ? (
                    <a 
                      href={`${API}/shop/download/${getPurchaseId(product.id)}`}
                      className="w-full"
                    >
                      <Button className="w-full bg-green-600 hover:bg-green-700">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </Button>
                    </a>
                  ) : (
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => handlePurchase(product.id)}
                      disabled={purchasing === product.id}
                      data-testid={`buy-${product.id}`}
                    >
                      {purchasing === product.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Acheter {product.price}€
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My Purchases Section */}
        {isAuthenticated && myPurchases.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading text-2xl font-semibold mb-6 flex items-center gap-2">
              <Download className="w-6 h-6 text-sage-600" />
              Mes achats
            </h2>
            
            <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100">
              {myPurchases.map((purchase) => (
                <div 
                  key={purchase.id}
                  className="p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {purchase.cover_image_url ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${purchase.cover_image_url}`}
                        alt={purchase.title}
                        className="w-12 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-purple-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{purchase.title}</p>
                      <p className="text-sm text-muted-foreground">
                        Acheté le {new Date(purchase.purchased_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  
                  <a href={`${API}/shop/download/${purchase.id}`}>
                    <Button variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
